import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { NotePencil, ArrowClockwise } from "@phosphor-icons/react";

const relTime = (iso) => {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function NotesTodayCard() {
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.get(`/crm/notes/recent?days=${days}&limit=20`);
      setNotes(data.notes || []);
    } catch (e) { setError(e?.response?.data?.detail || "Failed to load notes"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <section className="bg-white border border-slate-200 rounded-sm" data-testid="notes-today-card">
      <header className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-slate-200 flex-wrap">
        <div className="flex items-center gap-2">
          <NotePencil size={16} className="text-blue-600"/>
          <h2 className="font-display font-bold text-slate-900">Notes today</h2>
          <span className="text-[10px] font-semibold uppercase text-slate-400">Recent deal notes across the CRM</span>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="notes-days" className="text-xs border border-slate-300 rounded-sm px-2 py-1">
            <option value="1">Last 24h</option>
            <option value="3">Last 3 days</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
          </select>
          <button onClick={load} disabled={busy} data-testid="notes-refresh" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-100 disabled:opacity-50">
            <ArrowClockwise size={11} className={busy ? "animate-spin" : ""}/>Refresh
          </button>
        </div>
      </header>
      <div>
        {error && <div className="p-4 text-sm text-red-700">{error}</div>}
        {!error && notes.length === 0 && !busy && (
          <div className="px-5 py-8 text-center text-sm text-slate-400" data-testid="notes-empty">No new notes in the selected window.</div>
        )}
        <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
          {notes.map((n) => (
            <li key={n.id} className="px-4 sm:px-5 py-3 hover:bg-blue-50/50" data-testid={`note-row-${n.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/crm?tab=deals&hs_id=${n.deal_hs_id}`} className="text-sm font-semibold text-blue-700 hover:underline truncate block">{n.dealname || `Deal ${n.deal_hs_id}`}</Link>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words">{n.note}</p>
                </div>
                <span className="text-[11px] text-slate-500 shrink-0 tabular-nums">{relTime(n.at)}</span>
              </div>
              {n.by && <div className="text-[11px] text-slate-400 mt-1">by {n.by}</div>}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
